import { useQuery } from '@apollo/client/react';
import { PageTitle } from '@optools/components/layout/typography';
import { FilteredSearch } from '@optools/components/search/filtered-search';
import { Separator } from '@optools/components/ui/separator';
import { cn } from '@optools/components/ui/utils';
import { GET_REGIONAL_PIPELINE_CONFIGS } from '@optools/services/operator-gateway/pipeline-config/queries';
import { useTeamsQuery } from '@optools/store/statsApiSlice';
import { PipelineConfigSheet } from '@optools/tools/btc-tools/components/pipeline-config-sheet';
import {
  TeamConfigCard,
  TeamConfigGrid,
} from '@optools/tools/btc-tools/components/team-config-card';
import { groupPipelineConfigsByTeam } from '@optools/tools/btc-tools/utils/pipeline-configs';
import { filterTeamsBySearchTerm } from '@optools/utils/filter';
import { hydrateWithTeams } from '@optools/utils/teams';
import { createFileRoute } from '@tanstack/react-router';
import { Database } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_auth/btc-tools/configs/pipeline')({
  component: RouteComponent,
});

export function RouteComponent() {
  const { data: teams = [] } = useTeamsQuery();
  const { data } = useQuery(GET_REGIONAL_PIPELINE_CONFIGS);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);

  const summaries = [...(data?.east ?? []), ...(data?.central ?? [])];
  const groupedConfigs = groupPipelineConfigsByTeam(summaries);
  const hydratedConfigs = hydrateWithTeams(groupedConfigs, teams);

  const selectedConfig = groupedConfigs.find((config) => config.teamId === selectedTeamId);
  const configuredRegions =
    selectedConfig?.regions.filter(({ id }) => id != null).map(({ region }) => region) ?? [];

  return (
    <div className="space-y-4">
      <PageTitle>Pipeline Configs</PageTitle>
      <FilteredSearch
        data={hydratedConfigs}
        filterFn={(configs, term) =>
          filterTeamsBySearchTerm(configs, term, (config) =>
            config.regions.map((region) => region.id).filter((id): id is number => id != null),
          )
        }
        placeholder="Search teams or config ID">
        {(filteredConfigs) => (
          <TeamConfigGrid>
            {filteredConfigs.map((config) => (
              <button
                key={config.teamId}
                aria-label={`Team ${config.teamId} card`}
                className="group text-left"
                onClick={() => setSelectedTeamId(config.teamId)}>
                <TeamConfigCard
                  className="h-full transition-colors group-hover:bg-muted"
                  team={config.team}
                  teamId={config.teamId}>
                  <Separator className="my-4" />
                  <div className="space-y-2">
                    {config.regions.map(({ id, region }) => (
                      <div
                        key={`${config.teamId}-${region}`}
                        className="flex items-center justify-between text-sm"
                        data-testid={`${config.teamId}-${region}`}>
                        <span className="flex items-center gap-1 font-bold">
                          <Database className="size-4" />
                          {region}
                        </span>
                        <span className={cn(id == null && 'text-muted-foreground')}>
                          {id == null ? 'No config' : `Config ID: ${id}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </TeamConfigCard>
              </button>
            ))}
          </TeamConfigGrid>
        )}
      </FilteredSearch>

      <PipelineConfigSheet
        configuredRegions={configuredRegions}
        open={selectedTeamId != null}
        teamId={selectedTeamId}
        onOpenChange={() => setSelectedTeamId(null)}
      />
    </div>
  );
}
